/**
 * MESP Onboarding Primitives — Unified Design System integration (Feb 2026)
 *
 * Building blocks for Auth & Registration journey:
 *   • /login                  (Login)
 *   • /register?step=…        (RegistrationFlow Step 1 & Step 2)
 *   • /auth/callback          (AuthCallback)
 *   • /legacy-student         (LegacyStudentRegistration)
 *   • /forgot-password        (ForgotPassword)
 *   • /two-factor             (TwoFactorVerify)
 *
 * All components now resolve colours/spacing/radii from
 * `../ui-system/tokens.css` via CSS variables. The shell forces the
 * `.mesp-theme-dark` token context so every primitive renders the
 * unified Navy + Gold palette regardless of where it is used.
 *
 * The component API (props + data-testid contracts) is unchanged, so
 * all consumer pages continue to work without modification.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, AlertCircle, Check, Loader2, RefreshCw, ExternalLink, Home } from 'lucide-react';
import '../ui-system/tokens.css';
import { PhoneInput } from '../PhoneInput';

/* ──────────────────────────── Shell + Card ─────────────────────────── */

/**
 * OnboardingShell — Feb 2026 identity refresh.
 *
 * Renders the same atmospheric MESP scene used by the public
 * `WelcomeLanding` page (student walking toward the illuminated
 * university building at dusk) so every auth-flow page — Login,
 * Register, ForgotPassword, TwoFactorVerify — feels like a
 * continuous journey from the landing page instead of a jarring
 * switch into a "different app".
 *
 * Zero-cost properties
 * --------------------
 * • Reuses `/welcome-landing-bg.webp` — already fetched & cached by
 *   the browser during the landing render, so this page pays 0 extra
 *   network bytes.
 * • No new libraries, no new services, no auth-logic changes.
 * • Existing dark navy `--mesp-bg` shows behind if the image fails
 *   to load (safe fallback).
 *
 * Readability
 * -----------
 * A Navy gradient overlay (transparent → deep-navy) is layered over
 * the image so the form card in the middle/lower half of the screen
 * stays perfectly legible regardless of viewport height.
 */
export const OnboardingShell = ({ children, withTexture = true }) => (
  <div
    className="mesp-theme-dark mesp-ui min-h-screen flex items-center justify-center p-4 md:p-8 relative overflow-hidden"
    dir="rtl"
    data-testid="onboarding-shell"
    style={{ background: 'var(--mesp-bg)', color: 'var(--mesp-text)' }}
  >
    {withTexture && (
      <>
        {/* Atmospheric MESP scene — same asset the landing page uses. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url('/welcome-landing-bg.webp')",
            backgroundSize: 'cover',
            backgroundPosition: 'top center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.55,
            filter: 'blur(1px)',
          }}
        />
        {/* Deep navy gradient overlay to guarantee form legibility. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,17,40,0.55) 0%, rgba(10,17,40,0.78) 45%, rgba(10,17,40,0.94) 100%)',
          }}
        />
        {/* Soft gold vignette to echo the landing warmth. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 15%, rgba(201,162,78,0.10) 0%, transparent 55%)',
          }}
        />
      </>
    )}
    <div className="w-full max-w-lg z-10">{children}</div>
  </div>
);

export const OnboardingHeader = ({ title, subtitle, icon: Icon = BookOpen }) => (
  <div className="text-center mb-8">
    <div
      className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
      style={{
        background:
          'radial-gradient(circle at center, rgba(201,162,78,0.22), rgba(10,17,40,0.85))',
        border: '1.5px solid var(--mesp-gold)',
        boxShadow:
          '0 0 24px rgba(201, 162, 78, 0.28), 0 4px 16px rgba(0, 0, 0, 0.35)',
      }}
    >
      <Icon className="w-8 h-8" style={{ color: 'var(--mesp-gold)' }} />
    </div>
    <h1
      className="text-3xl font-bold tracking-tight mb-2"
      style={{
        color: 'var(--mesp-text)',
        textShadow: '0 2px 12px rgba(0, 0, 0, 0.55)',
      }}
    >
      {title}
    </h1>
    {subtitle && (
      <p style={{ color: 'var(--mesp-text-muted)', textShadow: '0 1px 8px rgba(0, 0, 0, 0.5)' }}>
        {subtitle}
      </p>
    )}
  </div>
);

/**
 * "الرجوع للصفحة الرئيسية" — back-to-home link for registration pages.
 * Used so students who picked the wrong registration path (e.g. New
 * Student instead of Legacy Student) can quickly return to the entry
 * point (the Login page, which shows both registration CTAs).
 */
export const BackToHomeLink = ({ to = '/login', testId = 'back-to-home-link' }) => (
  <div className="mb-4 flex justify-start" dir="rtl">
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors"
      style={{
        color: 'var(--mesp-gold)',
        border: '1px solid var(--mesp-border-strong)',
        background: 'var(--mesp-bg-elevated)',
      }}
      data-testid={testId}
    >
      <Home className="w-4 h-4" />
      <span>الرجوع للصفحة الرئيسية</span>
    </Link>
  </div>
);

/**
 * OnboardingCard — Feb 2026 identity refresh.
 *
 * Sits over the atmospheric shell as a translucent, backdrop-blurred
 * "frosted glass" card with a soft gold border and gold top-accent so
 * it feels like a natural continuation of the landing page's ornate
 * gold-on-navy palette. No structural change — just visual polish.
 */
export const OnboardingCard = ({ children, className = '' }) => (
  <div
    className={`rounded-2xl overflow-hidden ${className}`}
    style={{
      background: 'rgba(15, 24, 51, 0.72)',
      border: '1px solid rgba(201, 162, 78, 0.28)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
      boxShadow:
        '0 20px 60px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(201, 162, 78, 0.08) inset',
    }}
    data-testid="onboarding-card"
  >
    <div
      className="h-1 w-full"
      style={{
        background: 'linear-gradient(90deg, #B5901C, var(--mesp-gold), #F3E5AB)',
      }}
    />
    <div className="p-6 md:p-8">{children}</div>
  </div>
);

export const OnboardingFooterLinks = () => (
  <div
    className="mt-8 flex justify-center gap-4 text-sm"
    style={{ color: 'var(--mesp-text-muted)' }}
  >
    <Link
      to="/privacy"
      className="transition-colors hover:opacity-100"
      style={{ color: 'var(--mesp-text-muted)' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--mesp-gold)')}
      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--mesp-text-muted)')}
    >
      سياسة الخصوصية
    </Link>
    <span>•</span>
    <Link
      to="/terms"
      className="transition-colors"
      style={{ color: 'var(--mesp-text-muted)' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--mesp-gold)')}
      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--mesp-text-muted)')}
    >
      شروط الاستخدام
    </Link>
  </div>
);

/* ──────────────────────────── Inputs ──────────────────────────────── */

export const OnboardingInput = React.forwardRef(function OnboardingInput(
  { label, required, helper, error, dir, className = '', style: styleProp, ...rest },
  ref,
) {
  const id = rest.id || rest.name || rest['data-testid'];
  // iOS Safari fix — see CHANGELOG. Forces light text + caret regardless
  // of system dark-mode auto-inversion.
  const iosSafeStyle = {
    color: '#FFFFFF',
    WebkitTextFillColor: '#FFFFFF',
    caretColor: 'var(--mesp-gold)',
    colorScheme: 'dark',
    fontSize: '16px',
    ...(styleProp || {}),
  };
  return (
    <div className="space-y-2">
      {label && (
        <label
          htmlFor={id}
          className="flex items-center gap-1 text-sm font-medium"
          style={{ color: 'var(--mesp-text)' }}
        >
          {label}
          {required && <span style={{ color: 'var(--mesp-gold)' }}>*</span>}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        dir={dir}
        className={`w-full px-4 py-3 transition-all focus:outline-none ${className}`}
        style={{
          background: 'rgba(10, 17, 40, 0.5)',
          border: '1px solid var(--mesp-border)',
          borderRadius: 'var(--mesp-radius-md)',
          ...iosSafeStyle,
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = 'var(--mesp-gold)';
          e.currentTarget.style.boxShadow = '0 0 0 1px var(--mesp-gold)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = 'var(--mesp-border)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        {...rest}
      />
      {helper && !error && (
        <p className="text-xs mt-1" style={{ color: 'var(--mesp-text-muted)' }}>{helper}</p>
      )}
      {error && (
        <p className="text-xs mt-1" style={{ color: 'var(--mesp-danger)' }}>{error}</p>
      )}
    </div>
  );
});

/* Wraps a leading addon (e.g. +966 flag) with an OnboardingInput. */
export const OnboardingPhoneRow = ({ label, required, addon, helper, error, style: styleProp, ...rest }) => {
  const id = rest['data-testid'] || rest.name;
  const iosSafeStyle = {
    color: '#FFFFFF',
    WebkitTextFillColor: '#FFFFFF',
    caretColor: 'var(--mesp-gold)',
    colorScheme: 'dark',
    fontSize: '16px',
    ...(styleProp || {}),
  };
  return (
    <div className="space-y-2">
      {label && (
        <label
          htmlFor={id}
          className="flex items-center gap-1 text-sm font-medium"
          style={{ color: 'var(--mesp-text)' }}
        >
          {label}
          {required && <span style={{ color: 'var(--mesp-gold)' }}>*</span>}
        </label>
      )}
      <div className="relative flex" dir="ltr">
        <div
          className="flex items-center justify-center px-3 gap-2"
          style={{
            borderTop: '1px solid var(--mesp-border)',
            borderBottom: '1px solid var(--mesp-border)',
            borderLeft: '1px solid var(--mesp-border)',
            borderTopLeftRadius: 'var(--mesp-radius-md)',
            borderBottomLeftRadius: 'var(--mesp-radius-md)',
            background: '#1A2342',
            color: 'var(--mesp-text)',
          }}
        >
          {addon}
        </div>
        <input
          id={id}
          inputMode="tel"
          autoComplete="tel-national"
          className="w-full px-4 py-3 transition-all focus:outline-none"
          style={{
            background: 'rgba(10, 17, 40, 0.5)',
            borderTop: '1px solid var(--mesp-border)',
            borderBottom: '1px solid var(--mesp-border)',
            borderRight: '1px solid var(--mesp-border)',
            borderTopRightRadius: 'var(--mesp-radius-md)',
            borderBottomRightRadius: 'var(--mesp-radius-md)',
            ...iosSafeStyle,
          }}
          {...rest}
        />
      </div>
      {helper && !error && (
        <p className="text-xs mt-1" style={{ color: 'var(--mesp-text-muted)' }}>{helper}</p>
      )}
      {error && (
        <p className="text-xs mt-1" style={{ color: 'var(--mesp-danger)' }}>{error}</p>
      )}
    </div>
  );
};

/* ── International phone with country picker (Feb 2026) ──────────────
   Replaces the static "+966 SA" addon pattern. Country code is fully
   selectable; pasted international prefixes ("+967…" / "00967…") are
   auto-detected by the underlying `PhoneInput`. Backend already
   normalises any incoming string via `phone_utils.normalize_to_e164`,
   so this component sends a clean E.164 string (or "" / null). */
export const OnboardingPhoneIntl = ({
  label,
  required,
  value,
  onChange,
  helper,
  error,
  defaultCountry = 'SA',
  placeholder,
  testId,
  disabled,
}) => (
  <div className="space-y-2">
    {label && (
      <label
        className="flex items-center gap-1 text-sm font-medium"
        style={{ color: 'var(--mesp-text)' }}
      >
        {label}
        {required && <span style={{ color: 'var(--mesp-gold)' }}>*</span>}
      </label>
    )}
    <PhoneInput
      variant="dark"
      value={value || ''}
      onChange={(e164) => onChange?.(e164 || '')}
      defaultCountry={defaultCountry}
      placeholder={placeholder || '5XXXXXXXX'}
      testId={testId || 'onboarding-phone'}
      disabled={disabled}
      invalid={!!error}
    />
    {helper && !error && (
      <p className="text-xs mt-1" style={{ color: 'var(--mesp-text-muted)' }}>{helper}</p>
    )}
    {error && (
      <p className="text-xs mt-1" style={{ color: 'var(--mesp-danger)' }}>{error}</p>
    )}
  </div>
);

/* ──────────────────────────── Pills (radio) ───────────────────────── */

export const RadioPills = ({ name, value, onChange, options, label, required, testIdPrefix }) => (
  <div className="space-y-2">
    {label && (
      <label
        className="flex items-center gap-1 text-sm font-medium"
        style={{ color: 'var(--mesp-text)' }}
      >
        {label}
        {required && <span style={{ color: 'var(--mesp-gold)' }}>*</span>}
      </label>
    )}
    <div
      className="flex p-1"
      style={{
        background: 'rgba(10, 17, 40, 0.5)',
        border: '1px solid var(--mesp-border)',
        borderRadius: 'var(--mesp-radius-md)',
      }}
      data-testid={testIdPrefix ? `${testIdPrefix}-group` : undefined}
    >
      {options.map((opt) => {
        const checked = value === opt.value;
        return (
          <label
            key={opt.value}
            className="flex-1 cursor-pointer"
            data-testid={testIdPrefix ? `${testIdPrefix}-${opt.value.toLowerCase()}` : undefined}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={checked}
              onChange={() => onChange(opt.value)}
              className="peer sr-only"
            />
            <div
              className="text-center py-2 text-sm font-medium transition-all"
              style={{
                borderRadius: 'var(--mesp-radius-sm)',
                background: checked ? 'var(--mesp-gold-soft)' : 'transparent',
                color: checked ? 'var(--mesp-gold)' : 'var(--mesp-text-muted)',
                border: checked ? '1px solid var(--mesp-gold-border)' : '1px solid transparent',
              }}
            >
              {opt.label}
            </div>
          </label>
        );
      })}
    </div>
  </div>
);

/* ──────────────────────────── Buttons ─────────────────────────────── */

export const GoldButton = ({ children, loading, disabled, className = '', ...rest }) => (
  <button
    disabled={disabled || loading}
    className={`w-full flex items-center justify-center gap-2 py-3.5 px-4 font-bold text-base md:text-lg transition-all transform disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${className}`}
    style={{
      background: 'linear-gradient(90deg, #B5901C, var(--mesp-gold), #B5901C)',
      color: '#0A1128',
      borderRadius: 'var(--mesp-radius-md)',
      boxShadow: 'var(--mesp-shadow-button)',
      border: 'none',
    }}
    onMouseEnter={(e) => {
      if (!disabled && !loading) {
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(201, 162, 78, 0.4)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = 'var(--mesp-shadow-button)';
      e.currentTarget.style.transform = 'translateY(0)';
    }}
    {...rest}
  >
    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
    {children}
  </button>
);

export const GhostButton = ({ children, className = '', ...rest }) => (
  <button
    className={`w-full flex items-center justify-center gap-2 py-3 px-4 font-medium transition-colors ${className}`}
    style={{
      background: 'rgba(255, 255, 255, 0.05)',
      color: 'var(--mesp-text)',
      border: '1px solid rgba(255, 255, 255, 0.10)',
      borderRadius: 'var(--mesp-radius-md)',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.10)';
      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.20)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.10)';
    }}
    {...rest}
  >
    {children}
  </button>
);

/* ──────────────────────────── Alerts ──────────────────────────────── */

export const OnboardingAlert = ({ variant = 'error', title, children, action }) => {
  const styles = {
    error: {
      bg: 'var(--mesp-danger-soft)',
      border: 'var(--mesp-danger-border)',
      iconColor: 'var(--mesp-danger)',
      titleColor: 'var(--mesp-text)',
      bodyColor: 'var(--mesp-text-muted)',
    },
    warn: {
      bg: 'var(--mesp-warning-soft)',
      border: 'var(--mesp-warning-border)',
      iconColor: 'var(--mesp-warning)',
      titleColor: 'var(--mesp-text)',
      bodyColor: 'var(--mesp-text-muted)',
    },
    success: {
      bg: 'var(--mesp-success-soft)',
      border: 'var(--mesp-success-border)',
      iconColor: 'var(--mesp-success)',
      titleColor: 'var(--mesp-text)',
      bodyColor: 'var(--mesp-text-muted)',
    },
    info: {
      bg: 'var(--mesp-gold-soft)',
      border: 'var(--mesp-gold-border)',
      iconColor: 'var(--mesp-gold)',
      titleColor: 'var(--mesp-text)',
      bodyColor: 'var(--mesp-text-muted)',
    },
  }[variant] || {
    bg: 'var(--mesp-danger-soft)',
    border: 'var(--mesp-danger-border)',
    iconColor: 'var(--mesp-danger)',
    titleColor: 'var(--mesp-text)',
    bodyColor: 'var(--mesp-text-muted)',
  };

  return (
    <div
      className="mb-6 p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
      style={{
        background: styles.bg,
        border: `1px solid ${styles.border}`,
        borderRadius: 'var(--mesp-radius-md)',
      }}
      data-testid={`onboarding-alert-${variant}`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: styles.iconColor }} />
        <div>
          {title && (
            <h4 className="text-sm font-semibold" style={{ color: styles.titleColor }}>
              {title}
            </h4>
          )}
          {children && (
            <p className="text-xs mt-1 leading-relaxed" style={{ color: styles.bodyColor }}>
              {children}
            </p>
          )}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
};

/* ──────────────────────────── Google Identity Island ──────────────── */

export const GoogleIdentityIsland = ({ email, fullName, onSwitch, onShowHelp, switching, busy }) => (
  <div
    className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 mb-6 shadow-sm relative overflow-hidden"
    style={{
      background: '#FCFCFA',
      border: '1px solid #E5E7EB',
      borderRadius: 'var(--mesp-radius-md)',
    }}
    data-testid="google-identity-strip"
  >
    <div className="flex items-center gap-3 mb-3 sm:mb-0 flex-1 min-w-0">
      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shrink-0 border border-gray-100 shadow-sm">
        <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      </div>
      <div className="flex flex-col min-w-0">
        {fullName && <span className="text-sm font-bold text-gray-900 truncate">{fullName}</span>}
        <span className="text-xs text-gray-600 truncate" dir="ltr" data-testid="google-identity-email">{email || '—'}</span>
      </div>
    </div>
    <div className="flex gap-2 w-full sm:w-auto">
      <button
        type="button"
        onClick={onSwitch}
        disabled={busy || switching}
        className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg transition-colors flex-1 sm:flex-none disabled:opacity-50"
        data-testid="switch-google-account-btn"
      >
        <RefreshCw size={14} className={switching ? 'animate-spin' : ''} />
        {switching ? 'جارٍ التبديل…' : 'تغيير الحساب'}
      </button>
      {onShowHelp && (
        <button
          type="button"
          onClick={onShowHelp}
          className="inline-flex items-center justify-center text-xs text-gray-500 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 px-2 py-2 rounded-lg transition-colors"
          data-testid="switch-google-help-toggle"
          title="لم تظهر شاشة اختيار الحساب؟"
        >
          <ExternalLink size={14} />
        </button>
      )}
    </div>
  </div>
);

/* ──────────────────────────── Continue with Google btn ────────────── */

export const GoogleSignInButton = ({ onClick, label = 'المتابعة باستخدام Google', testId = 'google-sign-in-btn' }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full bg-white hover:bg-gray-50 active:bg-gray-100 px-4 py-3 font-medium flex items-center justify-center gap-3 text-gray-800 transition-colors shadow-sm border border-gray-200"
    style={{ borderRadius: 'var(--mesp-radius-md)' }}
    data-testid={testId}
  >
    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
    <span>{label}</span>
  </button>
);

/* ──────────────────────────── Section "label" ──────────────────────── */

export const SectionLabel = ({ children, icon: Icon }) => (
  <div
    className="flex items-center gap-2 text-xs font-semibold mb-3"
    style={{ color: 'var(--mesp-gold)' }}
  >
    {Icon && <Icon className="w-3.5 h-3.5" />}
    <span>{children}</span>
  </div>
);

/* ──────────────────────────── Success card ────────────────────────── */

export const OnboardingSuccessCard = ({ title, message, primaryActionLabel, primaryActionTo, icon: Icon = Check }) => (
  <OnboardingShell>
    <div
      className="w-full max-w-md mx-auto p-8 text-center relative overflow-hidden"
      style={{
        background: 'var(--mesp-bg-elevated)',
        border: '1px solid var(--mesp-border-strong)',
        borderRadius: 'var(--mesp-radius-lg)',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.5), 0 0 15px rgba(201, 162, 78,0.1)',
      }}
      data-testid="onboarding-success-card"
    >
      <div
        className="absolute top-0 left-0 w-full h-1"
        style={{ background: 'linear-gradient(90deg, #B5901C, var(--mesp-gold), #F3E5AB)' }}
      />
      <div
        className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6"
        style={{
          background: 'var(--mesp-gold-soft)',
          border: '1px solid var(--mesp-gold-border)',
        }}
      >
        <Icon className="w-10 h-10" style={{ color: 'var(--mesp-gold)' }} />
      </div>
      <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--mesp-text)' }}>{title}</h2>
      <p className="mb-8 leading-relaxed" style={{ color: 'var(--mesp-text-muted)' }}>{message}</p>
      {primaryActionLabel && primaryActionTo && (
        <Link
          to={primaryActionTo}
          className="inline-flex w-full items-center justify-center gap-2 py-3 px-4 font-bold transition-all transform hover:-translate-y-0.5"
          style={{
            background: 'linear-gradient(90deg, #B5901C, var(--mesp-gold), #B5901C)',
            color: '#0A1128',
            borderRadius: 'var(--mesp-radius-md)',
            boxShadow: 'var(--mesp-shadow-button)',
          }}
          data-testid="success-primary-action"
        >
          {primaryActionLabel}
        </Link>
      )}
    </div>
  </OnboardingShell>
);
